import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { postData } from './apiService';
import { jwtDecode } from 'jwt-decode';
import { tokenIsExpired } from './authUtils';

const SessionTimeout = () => {
    const { login, ClearToken, token, logout, companyName, isAuthenticated } = useAuth();
    const [showWarning, setShowWarning] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Session configuration - 1 hour inactivity timeout
    const INACTIVITY_TIMEOUT = 3600000; // 1 hour in milliseconds
    const WARNING_TIME = 300000; // 5 minutes warning before logout
    const COUNTDOWN_DURATION = 300; // 5 minutes countdown in seconds

    // Timers
    const warningTimerRef = useRef(null);
    const logoutTimerRef = useRef(null);
    const countdownTimerRef = useRef(null);
    const tokenCheckTimerRef = useRef(null);

    // Activity tracking
    const lastActivityRef = useRef(Date.now());

    const resetTimers = useCallback(() => {
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = null;
        }
        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
    }, []);

    const startCountdown = useCallback(() => {
        setCountdown(COUNTDOWN_DURATION);
        countdownTimerRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(countdownTimerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [COUNTDOWN_DURATION]);

    const checkTokenValidity = useCallback(() => {
        if (!token || !isAuthenticated) {
            return false;
        }

        try {
            if (tokenIsExpired(token)) {
                console.log('Token expired, logging out...');
                logout();
                return false;
            }
            return true;
        } catch (error) {
            console.error('Error checking token validity:', error);
            logout();
            return false;
        }
    }, [token, isAuthenticated, logout]);

    const handleActivity = useCallback(() => {
        if (!isAuthenticated || !token) {
            return;
        }

        // Check token validity first
        if (!checkTokenValidity()) {
            return;
        }

        lastActivityRef.current = Date.now();
        
        // Reset all timers
        resetTimers();
        setShowWarning(false);
        setCountdown(0);

        // Set warning timer for inactivity
        warningTimerRef.current = setTimeout(() => {
            // Double check if user is still authenticated
            if (isAuthenticated && token && !tokenIsExpired(token)) {
                setShowWarning(true);
                startCountdown();

                // Set logout timer
                logoutTimerRef.current = setTimeout(() => {
                    console.log('Session timeout - logging out due to inactivity');
                    setShowWarning(false);
                    logout();
                }, WARNING_TIME);
            }
        }, INACTIVITY_TIMEOUT - WARNING_TIME);

    }, [isAuthenticated, token, checkTokenValidity, resetTimers, startCountdown, logout, WARNING_TIME, INACTIVITY_TIMEOUT]);

    const handleRefresh = async () => {
        if (isRefreshing) return;
        
        setIsRefreshing(true);
        try {
            const jwtToken = await performTokenRefresh();
            if (jwtToken) {
                const responseData = jwtDecode(jwtToken);
                const keysToRemove = ['iss', 'exp', 'ttp', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name', 'aud'];
                const permissions = Object.keys(responseData).filter(
                    (key) => key !== 'companyId' && key !== 'userId' && key !== 'branchId' && !keysToRemove.includes(key)
                );

                const isSuper = responseData.isSuperAdmin?.toLowerCase() === "true";
                
                // Clear current auth state
                ClearToken();

                // Login with new token
                login(
                    responseData.companyId,
                    responseData.branchId,
                    responseData.userId,
                    isSuper,
                    responseData.superAdminCompanyId,
                    companyName,
                    permissions,
                    jwtToken,
                    responseData.roleName,
                    responseData.branchstate,
                    responseData.username,
                    responseData.isAppointmentNeeded
                );

                setShowWarning(false);
                setCountdown(0);
                resetTimers();
                
                // Restart activity monitoring
                setTimeout(() => {
                    handleActivity();
                }, 100);
            }
        } catch (error) {
            console.error('Token refresh failed:', error);
            logout();
        } finally {
            setIsRefreshing(false);
        }
    };

    const performTokenRefresh = async () => {
        if (!token) {
            throw new Error('No token available for refresh');
        }

        try {
            const decodedToken = jwtDecode(token);

            // Check if token is already expired
            if (tokenIsExpired(token)) {
                throw new Error('Token already expired');
            }

            const loginData = {
                companyId: decodedToken.companyId,
                branchId: decodedToken.branchId,
                userId: decodedToken.userId,
                username: decodedToken.username
            };

            const response = await postData('UserTokenApi', '', loginData, token);

            if (response.responseCode === '1111') {
                return response.response_data.token;
            } else {
                throw new Error(response.Message || 'Token refresh failed');
            }
        } catch (error) {
            console.error('Token refresh error:', error);
            throw error;
        }
    };

    const handleLogout = () => {
        resetTimers();
        setShowWarning(false);
        setCountdown(0);
        logout();
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // Set up activity listeners and token checking
    useEffect(() => {
        if (!isAuthenticated || !token) {
            resetTimers();
            return;
        }

        // Initial activity setup
        handleActivity();

        // Set up periodic token validation (every 30 seconds)
        tokenCheckTimerRef.current = setInterval(() => {
            checkTokenValidity();
        }, 30000);

        // Activity event listeners
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        
        const activityHandler = () => {
            handleActivity();
        };

        events.forEach(event => {
            document.addEventListener(event, activityHandler, true);
        });

        return () => {
            resetTimers();
            if (tokenCheckTimerRef.current) {
                clearInterval(tokenCheckTimerRef.current);
            }
            events.forEach(event => {
                document.removeEventListener(event, activityHandler, true);
            });
        };
    }, [isAuthenticated, token, handleActivity, checkTokenValidity, resetTimers]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            resetTimers();
            if (tokenCheckTimerRef.current) {
                clearInterval(tokenCheckTimerRef.current);
            }
        };
    }, [resetTimers]);

    const styles = {
        warningContainer: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(5px)',
        },
        warningBox: {
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
            maxWidth: '400px',
            width: '90%',
            border: '3px solid #ff6b6b',
        },
        title: {
            color: '#ff6b6b',
            fontSize: '20px',
            fontWeight: 'bold',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
        },
        message: {
            fontSize: '16px',
            marginBottom: '20px',
            color: '#333',
            lineHeight: '1.5',
        },
        countdown: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#ff6b6b',
            marginBottom: '20px',
            padding: '10px',
            backgroundColor: '#fff5f5',
            borderRadius: '8px',
            border: '2px solid #ff6b6b',
        },
        buttonContainer: {
            display: 'flex',
            gap: '15px',
            justifyContent: 'center',
        },
        button: {
            padding: '12px 24px',
            fontSize: '14px',
            cursor: 'pointer',
            borderRadius: '8px',
            border: 'none',
            fontWeight: 'bold',
            transition: 'all 0.3s ease',
            minWidth: '100px',
        },
        refreshButton: {
            background: '#4CAF50',
            color: 'white',
        },
        refreshButtonHover: {
            background: '#45a049',
        },
        logoutButton: {
            background: '#f44336',
            color: 'white',
        },
        logoutButtonHover: {
            background: '#da190b',
        },
        refreshingButton: {
            background: '#ccc',
            color: '#666',
            cursor: 'not-allowed',
        },
        warningIcon: {
            fontSize: '24px',
        }
    };

    if (!isAuthenticated || !showWarning) {
        return null;
    }

    return (
        <div style={styles.warningContainer}>
            <div style={styles.warningBox}>
                <div style={styles.title}>
                    <span style={styles.warningIcon}>⚠️</span>
                    Session Timeout Warning
                </div>
                <div style={styles.message}>
                    Your session will expire due to inactivity. You will be automatically logged out in:
                </div>
                <div style={styles.countdown}>
                    {formatTime(countdown)}
                </div>
                <div style={styles.buttonContainer}>
                    <button 
                        style={{
                            ...styles.button, 
                            ...(isRefreshing ? styles.refreshingButton : styles.refreshButton)
                        }} 
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                    >
                        {isRefreshing ? 'Refreshing...' : 'Stay Logged In'}
                    </button>
                    <button 
                        style={{...styles.button, ...styles.logoutButton}} 
                        onClick={handleLogout}
                        disabled={isRefreshing}
                    >
                        Logout Now
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SessionTimeout;
